import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { getSaBookingFieldLabel } from '../saBookingFieldLabels';
import type { SaBookingDetailPayload } from '../SuperAdminBookingGuestDetailsSection';

export const PAYMENT_CONFIG_SECTION_FIELDS = [
  'minConfirmPaymentPercentage',
  'paymentSlotOnePercentage',
  'paymentSlotTwoPercentage',
  'paymentSlotThreePercentage',
  'paymentSlotOneBeforeDays',
  'paymentSlotTwoBeforeDays',
  'fullPaymentBeforeDays',
  'paymentStatus',
  'paymentOverDueText',
] as const;

const PCT_FIELDS = [
  { key: 'minConfirm' as const, apiField: 'minConfirmPaymentPercentage' as const },
  { key: 'slot1' as const, apiField: 'paymentSlotOnePercentage' as const },
  { key: 'slot2' as const, apiField: 'paymentSlotTwoPercentage' as const },
  { key: 'slot3' as const, apiField: 'paymentSlotThreePercentage' as const },
];

const DAY_FIELDS = [
  { key: 'before1' as const, apiField: 'paymentSlotOneBeforeDays' as const },
  { key: 'before2' as const, apiField: 'paymentSlotTwoBeforeDays' as const },
  { key: 'fullBefore' as const, apiField: 'fullPaymentBeforeDays' as const },
];

type PaymentConfigForm = {
  minConfirm: number;
  slot1: number;
  slot2: number;
  slot3: number;
  before1: number;
  before2: number;
  fullBefore: number;
};

function parsePct(raw: unknown): number {
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function parseDay(raw: unknown): number {
  if (raw == null || raw === '') return 0;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

const ReadOnlyBookingField: FC<{ label: string; value: string }> = ({ label, value }) => {
  const display = value || '—';
  const multiline = display.length > 72 || display.includes('\n');
  return (
    <TextField
      label={label}
      value={display}
      fullWidth
      variant="outlined"
      size="small"
      multiline={multiline}
      minRows={multiline ? 2 : 1}
      maxRows={multiline ? 12 : 1}
      InputProps={{ readOnly: true }}
      sx={{
        '& .MuiInputBase-input': { cursor: 'default' },
        '& .MuiOutlinedInput-root': {
          bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
        },
      }}
    />
  );
};

export type SuperAdminBookingPaymentConfigSectionProps = {
  booking: Record<string, unknown> | null;
  orderReference: string;
  sectionTitle: string;
  defaultExpanded?: boolean;
  formatFieldValue: (raw: unknown, field: string) => string;
  onUpdated: (detail: SaBookingDetailPayload) => void;
};

export const SuperAdminBookingPaymentConfigSection: FC<SuperAdminBookingPaymentConfigSectionProps> = ({
  booking,
  orderReference,
  sectionTitle,
  defaultExpanded = false,
  formatFieldValue,
  onUpdated,
}) => {
  const [form, setForm] = useState<PaymentConfigForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!booking) {
      setForm(null);
      return;
    }
    setForm({
      minConfirm: parsePct(booking.minConfirmPaymentPercentage),
      slot1: parsePct(booking.paymentSlotOnePercentage),
      slot2: parsePct(booking.paymentSlotTwoPercentage),
      slot3: parsePct(booking.paymentSlotThreePercentage),
      before1: parseDay(booking.paymentSlotOneBeforeDays),
      before2: parseDay(booking.paymentSlotTwoBeforeDays),
      fullBefore: parseDay(booking.fullPaymentBeforeDays),
    });
  }, [booking]);

  const pctTotal = form ? form.minConfirm + form.slot1 + form.slot2 + form.slot3 : 0;
  const pctOk = form != null && Math.abs(pctTotal - 100) <= 0.02;
  const preventNumberInputScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.target as HTMLInputElement;
    target.blur();
  };
  const handleSave = useCallback(async () => {
    const ref = orderReference.trim();
    if (!form || !ref) return;
    if (Math.abs(form.minConfirm + form.slot1 + form.slot2 + form.slot3 - 100) > 0.02) {
      setSaveError(strings.sa_booking_lookup_payment_percent_sum);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const { data: res } = await axiosInstance.patch<SaBookingDetailPayload>(
        URLS.superAdminBookingPaymentConfig(ref),
        {
          minConfirmPaymentPercentage: form.minConfirm,
          paymentSlotOnePercentage: form.slot1,
          paymentSlotTwoPercentage: form.slot2,
          paymentSlotThreePercentage: form.slot3,
          paymentSlotOneBeforeDays: form.before1,
          paymentSlotTwoBeforeDays: form.before2,
          fullPaymentBeforeDays: form.fullBefore,
        },
      );
      onUpdated(res);
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { message?: string; error_description?: string } };
        message?: string;
      };
      const msg =
        err.response?.data?.message ?? err.response?.data?.error_description ?? err.message ?? 'Request failed';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [form, orderReference, onUpdated]);

  const rowCount = PAYMENT_CONFIG_SECTION_FIELDS.length;

  if (!booking || !form) {
    return null;
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        overflow: 'hidden',
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" component="div" fontWeight={600}>
          {sectionTitle}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({rowCount})
          </Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 2.5 }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {PCT_FIELDS.map(({ key, apiField }) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <TextField
                    label={getSaBookingFieldLabel(apiField)}
                    type="number"
                    onWheel={preventNumberInputScroll}
                    value={Number.isFinite(form[key]) ? String(form[key]) : '0'}
                    onChange={e => {
                      const v = parseFloat(e.target.value);
                      setForm(f => (f ? { ...f, [key]: Number.isFinite(v) ? v : 0 } : null));
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={saving}
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                </Grid>
              ))}
            </Grid>
            <Typography
              variant="caption"
              color={pctOk ? 'text.secondary' : 'error'}
              sx={{ display: 'block', mt: 0.75, ml: 0.5 }}>
              {strings.sa_booking_lookup_payment_percent_total.replace('{n}', pctTotal.toFixed(2))}
            </Typography>
          </Grid>

          {DAY_FIELDS.map(({ key, apiField }) => (
            <Grid item xs={12} md={4} key={key}>
              <TextField
                label={getSaBookingFieldLabel(apiField)}
                type="number"
                onWheel={preventNumberInputScroll}
                value={String(form[key])}
                onChange={e => {
                  const n = Number.parseInt(e.target.value, 10);
                  setForm(f => (f ? { ...f, [key]: Number.isFinite(n) && n >= 0 ? n : 0 } : null));
                }}
                fullWidth
                variant="outlined"
                size="small"
                disabled={saving}
                inputProps={{ min: 0 }}
              />
            </Grid>
          ))}

          <Grid item xs={12} md={6}>
            <ReadOnlyBookingField
              label={getSaBookingFieldLabel('paymentStatus')}
              value={formatFieldValue(booking.paymentStatus, 'paymentStatus')}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyBookingField
              label={getSaBookingFieldLabel('paymentOverDueText')}
              value={formatFieldValue(booking.paymentOverDueText, 'paymentOverDueText')}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 2,
            mt: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={saving || !pctOk}>
            {strings.sa_booking_lookup_save}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
